import { Box, FormHelperText } from '@mui/material';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import { Button } from '../../blocks/Button';
import { useToast } from '../../hooks/useToast';
import styles from './PaymentElementContainer.module.scss';
import {
	PaymentMethodType,
	PaymentElementContainerContactForCreateOrUpdateContactForCustomerFragmentFragment,
	PaymentElementContainerContactForGetQuickPaymentFragmentFragment,
	InstallmentAmountInput,
	usePaymentElementContainer_ValidatePaymentLazyQuery,
	usePaymentElementContainer_GetStripePaymentCapabilitiesQuery,
} from '#types';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { gql } from '@apollo/client';
import { FlattenedUser } from '../../types/user';
import { useSelector } from 'react-redux';
import PaymentValidationErrorModal from './PaymentValidationErrorModal';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { getPaymentMethodOrder, getStatementDescriptorCopy } from './helpers';
import rpcShared from '@rockpapercoin/rpc-shared';

type StripeCapabilities = Parameters<
	typeof rpcShared.invoices.hasOBOCapabilities
>[0];

const isStripeCapabilities = ( capabilities: {
	card_payments?: string | null;
	acss_debit_payments?: string | null;
} ): capabilities is StripeCapabilities =>
	capabilities.card_payments !== null &&
	capabilities.acss_debit_payments !== null;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentElementContainerContactForGetQuickPaymentFragment = gql`
	fragment paymentElementContainerContactForGetQuickPaymentFragment on ContactForGetQuickPayment {
		id
		firstName
		lastName
		customer {
			id
			email
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentElementContainerContactForCreateOrUpdateContactForCustomerFragment = gql`
	fragment paymentElementContainerContactForCreateOrUpdateContactForCustomerFragment on Contact {
		id
		firstName
		lastName
		customer {
			id
			email
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentElementContainerGetStripePaymentCapabilitiesQuery = gql`
	query paymentElementContainer_GetStripePaymentCapabilities(
		$where: OrganizationWhereUniqueInput!
	) {
		getStripePaymentCapabilities(where: $where) {
			card_payments
			acss_debit_payments
		}
	}
`;

// Make sure to request the id and status of the installment, as validatePayment can correct some out-of-sync installment statuses
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentElementContainerValidatePaymentQuery = gql`
	query paymentElementContainer_validatePayment(
		$where: ValidatePaymentInstallmentsInput!
	) {
		validatePayment(where: $where) {
			id
			status
		}
	}
`;

export type PaymentElementContainerProps = {
	submitButtonCopy: string;
	returnUrl: string;
	loading: boolean;
	onUpdatePaymentMethodType: (
		paymentMethodType: PaymentMethodType | undefined
	) => Promise<void>;
	setLocalStorage?: () => void;
	clientSecret?: string;
	deferredPayment?: {
		amount: number;
		abandonPaymentAttempt: () => void;
		createPaymentIntent: () => Promise<string | undefined>;
	};
	handleSuccess: ( paymentIntentId: string ) => void;
	contact:
	| PaymentElementContainerContactForCreateOrUpdateContactForCustomerFragmentFragment
	| PaymentElementContainerContactForGetQuickPaymentFragmentFragment
	| undefined
	| null;
	orgName: string;
	orgId?: string;
	paymentMethodType: PaymentMethodType | undefined;
	isAnonymousView: boolean;
	multiplePaymentsAtOnce?: boolean;
	installmentsForValidation?: InstallmentAmountInput[];
	guestToken?: string;
	sessionPaymentInstallmentId?: string;
};

const PaymentElementContainer: React.FC<PaymentElementContainerProps> = ( {
	submitButtonCopy,
	returnUrl,
	onUpdatePaymentMethodType,
	loading: propsLoading,
	setLocalStorage,
	clientSecret,
	handleSuccess,
	contact,
	orgName,
	orgId,
	paymentMethodType,
	isAnonymousView,
	multiplePaymentsAtOnce,
	installmentsForValidation,
	guestToken,
	sessionPaymentInstallmentId,
	deferredPayment,
} ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const [ checkoutError, setCheckoutError ] = useState<string | undefined>(
		undefined
	);
	const [ showValidationModal, setShowValidationModal ] = useState( false );
	const { showError } = useToast();
	const stripe = useStripe();
	const elements = useElements();
	const [ paymentMethodOrder, setPaymentMethodOrder ] = useState<
	PaymentMethodType[] | undefined
	>( undefined );
	const [ paymentElementComplete, setPaymentElementComplete ] = useState( false );
	const [ loading, setLoading ] = useState( true );
	const [ validatePayment ] =
		usePaymentElementContainer_ValidatePaymentLazyQuery();

	const {
		data: stripePaymentCapabilitiesData,
		error: stripePaymentCapabilitiesError,
	} = usePaymentElementContainer_GetStripePaymentCapabilitiesQuery( {
		variables: { where: { id: orgId || '' } },
		skip: !orgId,
	} );
	const stripeCapabilities =
		stripePaymentCapabilitiesData?.getStripePaymentCapabilities;
	const hasInternationalPayments =
		stripeCapabilities && isStripeCapabilities( stripeCapabilities )
			? rpcShared.invoices.hasOBOCapabilities( stripeCapabilities )
			: false;

	const { isPhone } = useDeviceDetection();

	const onCheckoutSubmit = useCallback( async () => {
		if (
			!stripe ||
			!elements ||
			( !clientSecret && !deferredPayment ) ||
			!contact
		) {
			showError( 'Stripe not initialized' );
			return;
		}
		if ( !paymentElementComplete ) {
			const message = 'Select or enter a payment method before submitting.';
			showError( message );
			setCheckoutError( message );
			return;
		}
		setLoading( true );
		setLocalStorage?.();

		// Validate payment if we have installments data
		if (
			installmentsForValidation &&
			installmentsForValidation.length > 0 &&
			sessionPaymentInstallmentId
		) {
			const validationResult = await validatePayment( {
				variables: {
					where: {
						installments: installmentsForValidation,
						sessionPaymentInstallmentId,
						isPayingWithCard:
							rpcShared.invoices.paymentMethodIsCard( paymentMethodType ),
						token: guestToken,
					},
				},
				fetchPolicy: 'network-only',
			} );

			// Check for GraphQL errors
			if ( validationResult.error || !validationResult.data?.validatePayment ) {
				setLoading( false );
				const unfilteredErrors = rpcShared.errorFiltering.filterApiErrors(
					{ errors: validationResult.error?.graphQLErrors },
					[
						'vendorHasStripeNeeds',
						'default',
						'cannotReceivePayments',
						'cannotPayDisputedInvoice',
					],
					{
						vendorHasStripeNeeds: () =>
							showError( rpcShared.strings.errorMessages.vendorHasStripeNeeds ),
						default: () => showError( rpcShared.strings.errorMessages.default ),
						cannotReceivePayments: () =>
							showError( rpcShared.strings.errorMessages.cannotReceivePayments ),
						cannotPayDisputedInvoice: () =>
							showError(
								rpcShared.strings.errorMessages.cannotPayDisputedInvoice
							),
					}
				);
				if ( ( unfilteredErrors.errors?.length ?? 0 ) > 0 ) {
					setShowValidationModal( true );
				}
				return;
			}
		}

		const submitResult = await elements.submit();
		if ( submitResult.error ) {
			setLoading( false );
			showError( submitResult.error.message );
			setCheckoutError( submitResult.error.message );
			return;
		}
		let confirmationClientSecret: string | undefined;
		try {
			confirmationClientSecret = deferredPayment
				? await deferredPayment.createPaymentIntent()
				: clientSecret;
		} catch {
			setLoading( false );
			showError( 'Unable to initialize payment. Please try again.' );
			return;
		}
		if ( !confirmationClientSecret ) {
			setLoading( false );
			showError( 'Unable to initialize payment. Please try again.' );
			return;
		}
		const res = await stripe.confirmPayment( {
			clientSecret: confirmationClientSecret,
			elements,
			confirmParams: {
				return_url: returnUrl,
				payment_method_data: {
					allow_redisplay: isAnonymousView ? 'limited' : 'always',
					billing_details: {
						email: contact.customer?.email,
						name: `${ contact.firstName } ${ contact.lastName }`,
					},
				},
			},
			redirect: 'if_required',
		} );
		setLoading( false );
		if ( res.error ) {
			deferredPayment?.abandonPaymentAttempt();
			// show the error
			showError( res.error.message );
			// refetch the installment statuses to ensure they are up to date
			if (
				installmentsForValidation &&
				installmentsForValidation.length > 0 &&
				sessionPaymentInstallmentId
			) {
				await validatePayment( {
					variables: {
						where: {
							installments: installmentsForValidation,
							sessionPaymentInstallmentId,
							isPayingWithCard:
								rpcShared.invoices.paymentMethodIsCard( paymentMethodType ),
							token: guestToken,
							// don't throw validation errors, just use validatePayment to update the installment statuses
							skipThrowingErrors: true,
						},
					},
					fetchPolicy: 'network-only',
				} );
			}
			// show checkout error
			if ( res.error.message ) {
				setCheckoutError( res.error.message );
			}
			return;
		} else if ( res.paymentIntent ) {
			handleSuccess( res.paymentIntent.id );
		}
	}, [
		stripe,
		elements,
		clientSecret,
		deferredPayment,
		contact,
		setLocalStorage,
		returnUrl,
		showError,
		handleSuccess,
		isAnonymousView,
		installmentsForValidation,
		validatePayment,
		paymentMethodType,
		guestToken,
		sessionPaymentInstallmentId,
		paymentElementComplete,
	] );

	useEffect( () => {
		if ( elements && deferredPayment ) {
			void elements.update( { amount: deferredPayment.amount } );
		}
	}, [ deferredPayment, elements ] );

	const handleValidationModalClose = useCallback( async () => {
		setShowValidationModal( false );
		// Clear invoice-related data from Apollo cache and refresh the page
		// Refresh the page to ensure latest data
		window.location.reload();
	}, [] );

	const onChange = useCallback(
		async ( e: StripePaymentElementChangeEvent ) => {
			setPaymentElementComplete( e.complete );
			const method =
				PaymentMethodType[ e.value.type as keyof typeof PaymentMethodType ];
			if ( method !== paymentMethodType ) {
				setLoading( true );
				await onUpdatePaymentMethodType( method );
				setCheckoutError( undefined );
				if ( !deferredPayment ) {
					await elements?.fetchUpdates();
				}
				setLoading( false );
			}
		},
		// do not include elements in the dependency array because it will cause a loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			onUpdatePaymentMethodType,
			paymentMethodType,
			deferredPayment
		]
	);

	const statementDescriptorCopy = useMemo(
		() =>
			getStatementDescriptorCopy( {
				orgName: orgName || '',
				paymentMethodType: paymentMethodType || PaymentMethodType.card,
				hasInternationalPayments,
			} ),
		[
			orgName,
			paymentMethodType,
			hasInternationalPayments
		]
	);

	useEffect( () => {
		if ( paymentMethodType ) {
			setPaymentMethodOrder( ( prevState ) =>
				getPaymentMethodOrder( prevState, paymentMethodType )
			);
		}
	}, [ paymentMethodType ] );

	useEffect( () => {
		if ( stripePaymentCapabilitiesError ) {
			showError( stripePaymentCapabilitiesError.message );
		}
	}, [ stripePaymentCapabilitiesError, showError ] );

	return paymentMethodOrder && paymentMethodType && contact ? (
		<>
			<form className={ styles.container }>
				<PaymentElement
					onChange={ onChange }
					onReady={ () => setLoading( false ) }
					options={ {
						paymentMethodOrder,
						layout: {
							type: isPhone ? 'accordion' : 'tabs',
							defaultCollapsed: user?.isLoggedIn,
						},
						fields: {
							billingDetails: {
								email: 'never',
								name: 'never',
							},
						},
						terms: {
							card: 'never',
							usBankAccount: 'never',
							applePay: 'never',
							googlePay: 'never',
						},
						...( multiplePaymentsAtOnce
							? {
								wallets: {
									applePay: 'never',
									googlePay: 'never',
								},
							  }
							: {} ),
					} }
				/>
				{ checkoutError ? (
					<FormHelperText error={ true } data-cy='checkout-error'>
						{ checkoutError }
					</FormHelperText>
				) : null }
				<FormHelperText error={ false } data-cy='statement-descriptor-copy'>
					{ statementDescriptorCopy }
				</FormHelperText>
				<Box className={ styles.payButtonContainer }>
					<Button
						className={ styles.payButton }
						data-cy='invoice-payment-button'
						variant='contained'
						color='primary'
						onClick={ ( e ) => {
							e.preventDefault();
							onCheckoutSubmit();
						} }
						type='submit'
						aria-label={ submitButtonCopy }
						loading={ loading || propsLoading }
					>
						{ submitButtonCopy }
					</Button>
				</Box>
			</form>
			<PaymentValidationErrorModal
				open={ showValidationModal }
				onClose={ handleValidationModalClose }
			/>
		</>
	) : null;
};

export default PaymentElementContainer;
